import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import {
  T,
  OPERATIONAL_STATUS,
  formatClock,
  formatRelative,
} from "../../constants";
import { useUi, cx } from "../../ui";
import { Loading, LoadingError } from "../../../../components/LoadingFork";
import Plate from "../../components/Plate";
import Tag from "../../components/Tag";
import VipBadge from "../../components/VipBadge";
import { arrivalEta, isUnmatched } from "../../helpers";
import useExpectedArrivals from "../useExpectedArrivals";
import { deadlineOf } from "../onSiteTabs";
import ColumnPanel from "./ColumnPanel";
import { useRowStyles } from "./boardRows";

const useStyles = makeStyles({
  views: {
    display: "flex",
    gap: 14,
    padding: "0 20px 8px",
    margin: 0,
    listStyle: "none",
  },
  view: {
    border: "none",
    background: "transparent",
    color: T.mutedFg,
    padding: "2px 0 6px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    borderBottom: "2px solid transparent",
    '&[aria-selected="true"]': {
      color: T.fg,
      fontWeight: 600,
      borderBottomColor: T.fg,
    },
  },
  actions: { display: "inline-flex", gap: 6 },
  errorState: { padding: "16px 20px" },
});

/* "failed" is a distinct state from "never charged" - a declined card is the
 * one the concierge actually needs to chase. */
function paymentLabel(booking) {
  if (booking.paymentRequired === false) {
    return { text: "no payment due", color: T.mutedFg };
  }
  if (booking.paid) return { text: "paid", color: T.successText };
  if (booking.paymentStatus === "failed") {
    return { text: "payment failed", color: T.danger };
  }

  return { text: "unpaid", color: T.warning };
}

const sameDay = (iso, now) => {
  if (!iso) return false;

  const at = new Date(iso);

  return (
    Number.isFinite(at.getTime()) &&
    at.toDateString() === new Date(now).toDateString()
  );
};

/**
 * Everything the desk is waiting on today: cars that have not arrived, and
 * cars whose pickup is on the way.
 */
export function departuresToday(cars = [], now = Date.now()) {
  return cars
    .filter((car) => sameDay(car.requestedAt, now))
    .sort((a, b) => (deadlineOf(a, now) ?? 0) - (deadlineOf(b, now) ?? 0));
}

export default function ExpectedColumn({
  hydrated,
  onSite = [],
  onOpenCar,
  onNewBooking,
  onNewArrival,
  now = Date.now(),
}) {
  const s = useStyles();
  const r = useRowStyles();
  const ui = useUi();
  const [view, setView] = useState("ARRIVALS");
  const { items, count, loading, loadingMore, error, hasMore, loadMore } =
    useExpectedArrivals();

  const departures = useMemo(() => departuresToday(onSite, now), [onSite, now]);

  const arrivals = view === "ARRIVALS";
  const shown = arrivals ? items.length : departures.length;

  const toolbar = (
    <ul className={s.views} role="tablist" aria-label="Expected view">
      <li>
        <button
          type="button"
          role="tab"
          className={s.view}
          aria-selected={arrivals}
          onClick={() => setView("ARRIVALS")}
        >
          Arrivals
        </button>
      </li>
      <li>
        <button
          type="button"
          role="tab"
          className={s.view}
          aria-selected={!arrivals}
          onClick={() => setView("DEPARTURES")}
        >
          Departures
        </button>
      </li>
    </ul>
  );

  if (error && arrivals) {
    return (
      <ColumnPanel
        title="Expected"
        count={0}
        noun="arriving today"
        toolbar={toolbar}
        itemCount={1}
      >
        <div className={s.errorState}>
          <LoadingError error={error} />
        </div>
      </ColumnPanel>
    );
  }

  return (
    <ColumnPanel
      title="Expected"
      count={arrivals ? count : departures.length}
      noun={arrivals ? "arriving today" : "departing today"}
      meta={
        arrivals && items.length < count ? `showing ${items.length}` : null
      }
      action={
        <span className={s.actions}>
          {onNewArrival && (
            <button
              type="button"
              className={cx(ui.mutedBtn, r.tiny)}
              onClick={onNewArrival}
            >
              + New arrival
            </button>
          )}
          <button
            type="button"
            className={cx(ui.primaryBtn, r.tiny)}
            onClick={onNewBooking}
          >
            + New booking
          </button>
        </span>
      }
      toolbar={toolbar}
      empty={
        arrivals
          ? "No expected arrivals right now."
          : "No departures on the board today."
      }
      itemCount={loading && arrivals ? 1 : shown}
    >
      {loading && arrivals && items.length === 0 ? (
        <Loading />
      ) : arrivals ? (
        <ul className={r.list} role="tabpanel">
          {items.map((booking) => {
            const pay = paymentLabel(booking);
            const eta = arrivalEta(booking);

            return (
              <li key={booking.id} data-row>
                <button
                  type="button"
                  className={r.row}
                  onClick={() => onOpenCar(booking)}
                >
                  <Plate size={11}>{booking.plate}</Plate>

                  <div className={r.main}>
                    <div className={r.line}>
                      <span className={r.name}>{booking.guestName}</span>
                      {booking.vip && <VipBadge />}
                      {isUnmatched(booking) && (
                        <Tag tone="bad" size={9} upper>
                          No booking
                        </Tag>
                      )}
                    </div>
                    {booking.notes && (
                      <div className={r.meta}>{booking.notes}</div>
                    )}
                  </div>

                  <div className={r.right}>
                    <span className={r.strong}>
                      {eta && hydrated ? formatRelative(eta) : "-"}
                    </span>
                    <span className={r.quiet} style={{ color: pay.color }}>
                      {pay.text}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}

          {hasMore && (
            <li className={r.loadMore}>
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className={ui.mutedBtn}
              >
                {loadingMore ? "Loading..." : "Load more"}
              </button>
            </li>
          )}
        </ul>
      ) : (
        <ul className={r.list} role="tabpanel">
          {departures.map((car) => (
            <li key={car.id} data-row>
              <button
                type="button"
                className={r.row}
                onClick={() => onOpenCar(car)}
              >
                <Plate size={11}>{car.plate}</Plate>

                <div className={r.main}>
                  <div className={r.line}>
                    <span className={r.name}>{car.guestName}</span>
                    {car.vip && <VipBadge />}
                  </div>
                  <div className={r.meta}>
                    {car.pickedByName ?? car.parkedLotName ?? "Not yet assigned"}
                  </div>
                </div>

                <div className={r.right}>
                  <span className={r.eyebrow}>Pickup</span>
                  <span className={r.status}>
                    {hydrated ? formatClock(car.requestedAt) : "-"}{" "}
                    · {OPERATIONAL_STATUS[car.status] ?? car.status}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </ColumnPanel>
  );
}
